import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ProductFormValues } from "../lib/schemas/travel-booking";
import { productSchema } from "../lib/schemas/travel-booking";
import {
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash } from "lucide-react";
import { getLiveRates } from "../apis";
import { FieldConfig, SelectOption } from "../lib/FormConfigs/config";
import { renderField } from "../lib/FormConfigs/Renderer";
import { FormField } from "../ui-form/form-field";
import { useAppDispatch } from "redux/hooks";
import { setForexOrderType } from "redux/features/CreateGICOrder";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { SelectContent, SelectItem, SelectTrigger, Select as ShadcnSelect } from "@/components/ui/select";

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (product: ProductFormValues) => void;
  initialData?: ProductFormValues[];
  order_type?: "buy" | "sell";
  config?: FieldConfig[];
}

export const FOREX_CURRENCIES_NOTES_OPTIONS = [
  { label: "USD", value: "USD" },
  { label: "EUR", value: "EUR" },
  { label: "GBP", value: "GBP" },
  { label: "AUD", value: "AUD" },
  { label: "CAD", value: "CAD" },
  { label: "SGD", value: "SGD" },
  { label: "NZD", value: "NZD" },
  { label: "HKD", value: "HKD" },
  { label: "AED", value: "AED" },
  { label: "SAR", value: "SAR" },
  { label: "CHF", value: "CHF" },
  { label: "SEK", value: "SEK" },
  { label: "THB", value: "THB" },
  { label: "JPY", value: "JPY" },
  { label: "MYR", value: "MYR" },
  { label: "CNY", value: "CNY" },
  { label: "ZAR", value: "ZAR" },
  { label: "OMR", value: "OMR" },
  { label: "BHD", value: "BHD" },
  { label: "KWD", value: "KWD" },
  { label: "NOK", value: "NOK" },
  { label: "DKK", value: "DKK" },
  { label: "IDR", value: "IDR" },
  { label: "LKR", value: "LKR" },
  { label: "KRW", value: "KRW" },
  { label: "TRY", value: "TRY" },
  { label: "RUB", value: "RUB" },
  { label: "QAR", value: "QAR" },
  { label: "PHP", value: "PHP" },
  { label: "VND", value: "VND" },
  { label: "MUR", value: "MUR" },
  { label: "BDT", value: "BDT" },
];

export const FOREX_CARD_CURRENCY_OPTIONS = [
  { label: "USD", value: "USD" },
  { label: "EUR", value: "EUR" },
  { label: "GBP", value: "GBP" },
  { label: "AUD", value: "AUD" },
  { label: "CAD", value: "CAD" },
  { label: "SGD", value: "SGD" },
  { label: "JPY", value: "JPY" },
  { label: "CHF", value: "CHF" },
  { label: "NZD", value: "NZD" },
  { label: "AED", value: "AED" },
  { label: "THB", value: "THB" },
  { label: "HKD", value: "HKD" },
  { label: "SAR", value: "SAR" },
  { label: "CNY", value: "CNY" },
];

const defaultProduct: ProductFormValues = {
  productType: { value: "", label: "" },
  product: { value: "sale", label: "Sale" },
  currencies: [{ currency: { value: "", label: "" }, amount: 0 }],
  local_id: "0",
};

export function ProductModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  config,
  order_type
}: ProductModalProps) {
  // State to track all products being added
  const dispatch = useAppDispatch()
  const [products, setProducts] = useState<ProductFormValues[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [currencyData, setCurrencyData] = useState<any>(null);
  const [initialDataProducts, setInitialDataProducts] = useState<ProductFormValues[]>([]);
  // Create a form specifically for this modal
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: defaultProduct,
    mode: "onChange",
  });

  const configFile = config ? config : [];

  // Reset products when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setProducts([]);
      setEditingIndex(null);
      setInitialDataProducts([]);
    }
  }, [isOpen]);

  const selectedProductType = form.watch("productType");
  const selectedProduct = form.watch("product");
  const currencies = form.watch("currencies") || [];

  // Fetch rates when product changes
  useEffect(() => {
    const refetchRatesForAllCurrencies = async () => {
      if (currencies.length > 0 && selectedProduct?.value) {
        const updatedCurrencies = await Promise.all(
          currencies.map(async (currency) => {
            if (currency.currency?.value) {
              const rate = await getLiveRates({
                currency: currency.currency.value.toUpperCase(),
                type: selectedProduct?.value === 'encashment' ? "Sell" : "Buy",
                fx_product:
                  selectedProductType?.label === "New card issuance"
                    ? "Card"
                    : "Currency",
              });
              return {
                ...currency,
                rate: Number(rate?.rates) || 0,
              };
            }
            return currency;
          })
        );
        form.setValue("currencies", updatedCurrencies, { shouldValidate: true });
      }
    };
    refetchRatesForAllCurrencies();
  }, [selectedProduct?.value, selectedProductType?.type]);

  useEffect(() => {
    if (initialData) {
      // Store the full initialData products
      setInitialDataProducts(initialData);
      handleCurrencyChange(0, { value: "", label: "" });
    } else {
      form.reset(defaultProduct);
      setInitialDataProducts([]);
      // Ensure product (sale) is set
      const productField = configFile?.find(item => item.name === 'product');
      if (productField && productField.type === 'select') {
        const saleOption = productField.options?.find((opt: any) => opt.value === 'sale');
        if (saleOption) {
          form.setValue('product', saleOption, { shouldValidate: true });
        }
      }
      // also make sure to fetch live rates for default currency
      handleCurrencyChange(0, { value: "USD", label: "USD" });
    }
  }, [initialData, form, order_type, configFile ]);

  const handleCurrencyChange = async (index: number, currencyOption: any) => {
    const updatedCurrencies = [...currencies];
    updatedCurrencies[index] = {
      ...updatedCurrencies[index],
      currency: currencyOption,
    };

    dispatch(setForexOrderType(selectedProductType?.type === "sell" ? "sell" : "buy"))
    if (currencyOption?.value) {
      const rate = await getLiveRates({
        currency: currencyOption?.value.toUpperCase(),
        type: selectedProduct?.value === 'encashment' ? "Sell" : "Buy",
        fx_product:
          selectedProductType?.label === "New card issuance"
            ? "Card"
            : "Currency",
      });
      updatedCurrencies[index].rate = Number(rate?.rates) || 0;
    }

    form.setValue("currencies", updatedCurrencies, { shouldValidate: true });
  };

  useEffect(() => {
    const run = async () => {
      if (currencyData?.id !== undefined && currencyData?.val) {
        await handleCurrencyChange(currencyData?.id, currencyData?.val);
        // Clear currencyData after processing to prevent re-triggering on product type change
        setCurrencyData(null);
      }
    };
    run();
  }, [currencyData, selectedProductType?.type]);

  const handleAmountChange = (index: number, amount: string) => {
    const updatedCurrencies = [...currencies];
    updatedCurrencies[index] = {
      ...updatedCurrencies[index],
      amount: Number.parseFloat(amount) || 0,
    };
    form.setValue("currencies", updatedCurrencies, { shouldValidate: true });
  };

  const handleRateChange = (index: number, rate: string) => {
    const updatedCurrencies = [...currencies];
    updatedCurrencies[index] = {
      ...updatedCurrencies[index],
      rate: Number.parseFloat(rate) || 0,
    };
    form.setValue("currencies", updatedCurrencies, { shouldValidate: true });
  };

  const addCurrency = () => {
    form.setValue(
      "currencies",
      [
        ...currencies,
        { currency: { value: "", label: "" }, amount: 0, rate: 0 },
      ],
      {
        shouldValidate: true,
      }
    );
  };

  const removeCurrency = (index: number) => {
    const updatedCurrencies = currencies.filter((_, i) => i !== index);
    form.setValue("currencies", updatedCurrencies, { shouldValidate: true });
  };

  // Add current product to the list
  const addProductToList = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      const productData = form.getValues();
      const random_id = crypto.randomUUID();

      const newProducts = editingIndex !== null
        ? [...products.slice(0, editingIndex), productData, ...products.slice(editingIndex + 1)]
        : [...products, { ...productData, local_id: random_id }];

      setProducts(newProducts);

      if (editingIndex !== null) {
        setEditingIndex(null);
      }

      // Check if USD is available, otherwise use first available

      const resetValues: ProductFormValues = {
        productType: { value: "", label: "" },
        product: selectedProduct,
        currencies: [
          {
            currency: { value: "", label: "" },
            amount: 0,
            rate: 0,
          }
        ],
        local_id: "0",
      }

      form.reset(resetValues);
    }
  };

  // Remove a product from the list
  const removeProduct = (index: number) => {
    const updatedProducts = products.filter((_, i) => i !== index);
    setProducts(updatedProducts);

    if (editingIndex === index) {
      setEditingIndex(null);
      form.reset(defaultProduct);
    }
  };

  // Submit all products
  const handleSubmitAll = () => {
    // If there's a product in the form that hasn't been added to the list yet
    if (
      form.formState.isValid
    ) {
      form.handleSubmit((data) => {
        const random_id = crypto.randomUUID();
        const productWithId = { ...data, local_id: random_id };
        const allProducts = [...products, productWithId];
        const productToSubmit = allProducts?.flatMap((p) => {
          if (p.cardType?.value === 'multicurrency') {
            // make new prodcut for each currency
            return p.currencies.map((c) => ({
              ...p,
              currencies: [{ ...c, local_id: crypto.randomUUID() }],
            }))
          } else {
            return p.local_id ? p : { ...p, local_id: crypto.randomUUID() }
          }
        })
        submitAllProducts(productToSubmit);
      })();
    } else {
      // Ensure all products have local_id (optional safety check)          
      const allProducts = products.flatMap((p) => {
        if (p.cardType?.value === 'multicurrency') {
          // make new prodcut for each currency
          return p.currencies.map((c) => ({
            ...p,
            currencies: [{ ...c, local_id: crypto.randomUUID() }],
          }))
        }
        return p.local_id ? p : { ...p, local_id: crypto.randomUUID() }
      });
      submitAllProducts(allProducts);
    }
  };

  const submitAllProducts = (productsToSubmit: ProductFormValues[]) => {
    // Here's the key fix - we need to handle all products at once
    productsToSubmit.forEach((product) => {
      onSubmit(product);
    });

    onClose();
    setProducts([]);

    const resetValues: ProductFormValues = {
      productType: selectedProductType,
      product: selectedProduct,
      currencies: [],
      local_id: "0",
    }

    form.reset(resetValues);
  };

  const isFormCompletelyFilled = () => {
    const values = form.getValues();
    console.log({ values });
    for (const field of configFile) {
      if (field.required && !values[field.name as keyof ProductFormValues]) {
        return false;
      }
    }
    if (!values?.product?.value || !values?.productType?.value) return false;
    if (!values.currencies || values.currencies.length === 0) return false;

    for (const currency of values.currencies) {
      if (!currency.currency.value || !currency.amount || currency.amount <= 0) {
        return false;
      }
    }
    return true;
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogPortal>
        <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
        <AlertDialogContent className="max-w-[880px] p-0 rounded-lg bg-white-A700">
          <AlertDialogHeader className="flex flex-row justify-between py-6 px-10 border-b">
            {/* Left Side: Title + Subtext */}
            <div className="flex flex-col items-start">
              <h2 className="text-xl font-semibold text-gray-900">
                Add Product
              </h2>
              <p className="text-sm text-left text-gray-500 mt-1">
                Fill the required details
              </p>
            </div>
          </AlertDialogHeader>
          <div className="px-10 overflow-auto max-h-[700px]">
            <Accordion type="single" collapsible className="space-y-2">
              {products?.length > 0
                ? products?.map((product, index) => (
                  <AccordionItem
                    className="bg-[#F9F9F9] no-underline border-0 py-3 px-5 rounded-lg"
                    key={index}
                    value={`item-${index}`}
                  >
                    <AccordionTrigger className="flex hover:no-underline justify-between w-full items-center p-0">
                      <div className="flex justify-between w-full items-center mr-2">
                        <div className="flex gap-2 items-center">
                          <p>{product.productType.label}</p>
                          <p className="text-blue-700 flex items-center gap-2">
                            <small className="bg-blue-700 text-[10px] text-white-A700 rounded-3xl px-2 py-0.5">
                              INR
                            </small>
                            {product.currencies
                              .reduce(
                                (sum, c) =>
                                  sum + (c?.amount || 0) * (c?.rate || 1),
                                0
                              )
                              .toFixed(2)}
                          </p>
                        </div>
                        <Button
                          variant='ghost'
                          size="sm"
                          onClick={() => removeProduct(index)}
                        >
                          <Trash className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Currency</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Rate</TableHead>
                            <TableHead>Total Value</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {product.currencies.map((currency, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{currency.currency.label}</TableCell>
                              <TableCell>{currency.amount}</TableCell>
                              <TableCell>{currency.rate}</TableCell>
                              <TableCell>
                                {(currency.amount * currency?.rate!).toFixed(
                                  2
                                )}
                              </TableCell>
                              <TableCell className="flex self-end space-x-2">
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionContent>
                  </AccordionItem>
                ))
                : null}
            </Accordion>
            <div className="grid grid-cols-1 gap-6 max-w-sm mt-2">
              {/* Product Form */}
              <div className="rounded-md w-full">
                <FormProvider {...form}>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      addProductToList();
                    }}
                  >
                    <div className="space-y-4 w-full relative">
                      {configFile?.map((item, index) => {
                        if (
                          item.name === "cardType"
                        ) {
                          return null;
                        }
                        if (item.name === "currencies") {
                          return (
                            <div
                              key="currencies-container"
                              className={`space-y-4 w-full ${currencies.length > 4 ? 'max-h-[400px] overflow-y-auto pr-2' : ''}`}
                            >
                              {currencies?.map((currency, idx) => {
                                // Filter out already selected currencies from current form
                                const selectedCurrenciesInForm = currencies
                                  .map((c, i) => i !== idx ? c.currency?.value : null)
                                  .filter(Boolean);

                                // Filter out currencies already used in products with the same product type
                                // Card products (16, 36) should share currency restrictions
                                // Currency products (9) should share currency restrictions
                                // But allow same currency across different product types
                                const currentProductTypeValue = selectedProductType?.value;
                                const isCardProduct = currentProductTypeValue === "16" || currentProductTypeValue === "36";
                                const isCurrencyProduct = currentProductTypeValue === "9";

                                const selectedCurrenciesInSameProductType = products
                                  .filter((p) => {
                                    const productTypeValue = p.productType?.value;
                                    if (isCardProduct) {
                                      // Filter currencies from other card products (16 or 36)
                                      return productTypeValue === "16" || productTypeValue === "36";
                                    } else if (isCurrencyProduct) {
                                      // Filter currencies from other currency products (9)
                                      return productTypeValue === "9";
                                    }
                                    // If product type doesn't match known types, don't filter
                                    return false;
                                  })
                                  .flatMap((p) => p.currencies.map((c) => c.currency?.value))
                                  .filter(Boolean);

                                // Filter currencies from initialData based on same product type
                                const selectedCurrenciesFromInitialData = initialDataProducts
                                  .filter((p) => {
                                    const productTypeValue = p.productType?.value;
                                    if (isCardProduct) {
                                      // Filter currencies from other card products (16 or 36)
                                      return productTypeValue === "16" || productTypeValue === "36";
                                    } else if (isCurrencyProduct) {
                                      // Filter currencies from other currency products (9)
                                      return productTypeValue === "9";
                                    }
                                    // If product type doesn't match known types, don't filter
                                    return false;
                                  })
                                  .flatMap((p) => p.currencies.map((c) => c.currency?.value))
                                  .filter(Boolean);

                                // Combine currencies from current form, same product type, and initialData
                                const allExcludedCurrencies = [
                                  ...selectedCurrenciesInForm,
                                  ...selectedCurrenciesInSameProductType,
                                  ...selectedCurrenciesFromInitialData
                                ];

                                const availableCurrencies = (selectedProductType?.value === "16"
                                  ? FOREX_CURRENCIES_NOTES_OPTIONS
                                  : FOREX_CARD_CURRENCY_OPTIONS
                                ).filter((curr) => !allExcludedCurrencies.includes(curr.value));

                                return (
                                  <FormItem
                                    key={idx}
                                    className="flex flex-col w-full gap-2 items-end rounded-md"
                                  >
                                    <FormLabel className="flex items-center gap-2 justify-between w-full">
                                      <span>
                                        Select the Currency.
                                        <span className="text-red-500">*</span>
                                      </span>
                                    </FormLabel>
                                    <FormControl className="w-full">
                                      <div className="relative w-full flex flex-col gap-2">
                                        <div className="relative w-full flex items-center">
                                          <ShadcnSelect
                                            disabled={form.watch("productType")?.value === ""}
                                            value={currency.currency?.value || ""}
                                            onValueChange={(val) => {
                                              setCurrencyData({ id: idx, val: { value: val, label: val } })
                                            }}
                                          >
                                            <SelectTrigger className="absolute left-3 top-1 z-10 flex h-10 w-[72px] items-center justify-start rounded-lg border-none bg-[#E1EBFC] px-3 text-sm font-medium text-[#165FE3] shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0">
                                              {currency.currency?.value || "Select"}
                                            </SelectTrigger>
                                            <SelectContent className="max-h-60">
                                              {availableCurrencies.map((curr) => (
                                                <SelectItem
                                                  key={curr.value}
                                                  value={curr.value}
                                                >
                                                  {curr.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </ShadcnSelect>

                                          <Input
                                            type="number"
                                            value={currency.amount || ""}
                                            onChange={(e) =>
                                              handleAmountChange(idx, e.target.value)
                                            }
                                            placeholder="Select the Qty"
                                            className="h-12 w-full rounded-lg border border-gray-border-gray-lite pl-[90px] pr-3 text-sm placeholder:text-[#9CA3AF] focus-visible:ring-0 focus-visible:ring-offset-0"
                                          />
                                          <div className="col-span-1 flex items-end">
                                            {idx > 0 && (
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => removeCurrency(idx)}
                                              >
                                                <Trash className="h-4 w-4 text-red-500" />
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                        
                                        {currency?.rate !== undefined && (
                                          <div className="flex items-center gap-2 w-full">
                                            <div className="flex-1 w-full">
                                              <Input
                                                type="number"
                                                step="0.0001"
                                                value={currency.rate || ""}
                                                onChange={(e) =>
                                                  handleRateChange(idx, e.target.value)
                                                }
                                                placeholder="Enter rate"
                                                className="h-10 w-full rounded-lg border border-gray-border-gray-lite px-3 text-sm placeholder:text-[#9CA3AF] focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-0"
                                              />
                                            </div>
                                            <div className="bg-[#FFFED9] py-2 px-3 rounded-md text-sm whitespace-nowrap">
                                              Live rate: <span className="text-blue-700 font-medium">{currency?.rate}</span>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </FormControl>
                                    <FormMessage />

                                  </FormItem>
                                );
                              })}
                            </div>
                          );
                        }

                        if (item.name === 'productType' && item.type === 'select') {
                          const filteredOptionsBasedOnProduct = item.options?.filter((opt: any) => {
                            if (selectedProduct?.value === 'sale') {
                              return opt.type === 'buy';
                            }
                            return opt.type === 'sell';
                          })
                          return (
                            <Tabs onValueChange={val => {
                              form.setValue('productType', filteredOptionsBasedOnProduct.find((opt: any) => opt.value === val) as SelectOption, { shouldValidate: true });
                            }} value={selectedProductType?.value} key={index} className="w-full">
                              <TabsList className="gap-4 flex-col items-start mb-3">
                                <FormLabel className="required-field">Select Product Type</FormLabel>
                                <div className="flex justify-start gap-2">
                                  {
                                    filteredOptionsBasedOnProduct?.map((option: any) => (
                                      <TabsTrigger
                                        key={option.id}
                                        value={option.value}
                                        className={`${((selectedProductType?.value === option.value))
                                          ? "bg-blue-700 text-white-A700"
                                          : "bg-gray-100 text-gray-700"
                                          } px-4 py-2 rounded-md text-sm font-medium`}
                                      >
                                        {option.label}
                                      </TabsTrigger>
                                    ))
                                  }
                                </div>
                              </TabsList>
                            </Tabs>
                          )
                        }

                        if (item.name === 'product' && item.type === 'select') {
                          const orderType = products?.[0]?.productType?.type || order_type;
                          const filteredProductOptionsBasedOnType = item.options?.filter((opt: any) => {
                            if (orderType) {
                              return (opt?.value === 'sale' ? 'buy' : 'sell') === orderType;
                            }
                            return true;
                          })
                          return (
                            <Tabs onValueChange={val => {                              
                              const foundOption = item.options.find((opt: any) => opt.value === val);
                              if (foundOption) {
                                form.setValue('product', foundOption, { shouldValidate: true });                                
                              }
                            }} value={selectedProduct?.value} key={index} className="w-full">
                              <TabsList className="flex justify-start gap-2">
                                {
                                  filteredProductOptionsBasedOnType?.map((option: any) => (
                                    <TabsTrigger
                                      key={option.id}
                                      value={option.value}
                                      className={`${((selectedProduct?.value === option.value))
                                        ? "bg-blue-700 text-white-A700"
                                        : "bg-gray-100 text-gray-700"
                                        } px-4 py-2 rounded-md text-sm font-medium`}
                                    >
                                      {option.label}
                                    </TabsTrigger>
                                  ))
                                }
                              </TabsList>
                            </Tabs>
                          )
                        }

                        return (
                          <FormField
                            key={index}
                            isOptionalField={item.required}
                            name={item.name}
                            label={item.label}
                            render={({ field }) => {
                              if (item.name === "productType" && item.type === 'select') {
                                let options = item.options;
                                const orderType = products?.[0]?.productType?.type || order_type;
                                if (orderType) {
                                  options = options.filter((option: any) => option.type === orderType);
                                }
                                return renderField({ ...item, options } as FieldConfig, field)
                              }
                              return renderField(item, field)
                            }}
                          />
                        );
                      })}
                      {['16', '36']?.includes(form.watch('productType.value')) && (
                        <div className="inline-flex !mb-5 items-center border border-blue-600 text-blue-600 rounded-lg px-3 py-1 cursor-pointer hover:bg-blue-50 transition">
                          <span className="text-lg font-bold mr-1">+</span>
                          <Button
                            type="button"
                            variant="ghost"
                            className="text-blue-600 px-0 py-0 h-auto"
                            onClick={addCurrency}
                            disabled={currencies.some((c) => !c.currency?.value)}
                          >
                            Add Currency
                          </Button>
                        </div>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={!form.formState.isValid || !isFormCompletelyFilled()}
                      className="bg-blue-700 mt-2 justify-end text-white-A700 border border-blue-700"
                    >
                      {editingIndex !== null ? "Update Product" : "Add product"}
                    </Button>
                  </form>
                </FormProvider>
              </div>
            </div>
          </div>
          <AlertDialogFooter className="h-min flex items-center gap-2 flex-row py-5 px-10">
            <Button
              className="text-blue-700 border border-blue-700"
              type="button"
              variant="outline"
              onClick={() => {
                // setProducts([]);
                // form.reset(defaultProduct);
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-700 text-white-A700 border border-blue-700"
              type="button"
              onClick={handleSubmitAll}
              disabled={
                products.length === 0 && !isFormCompletelyFilled()
              }
            >
              Save
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
}
